/**
 * Inventory Planning Service - Auxiliar para manejo de filtros y planificación
 * Maneja filtros avanzados, restricciones dinámicas y visualización del inventario
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { inventoryService } from './inventoryService';
import { inventoryAuxiliarApi, inventoryCalendarApi, constantsApi } from '@/utils/apiService';
// Restricciones soportadas por Channex
export var RESTRICTION_OPTIONS = [
    // Valid Channex restriction types only
    // { value: 'rate', label: 'Rate Override', category: 'rate', input_type: 'number', default_value: 0 },
    { value: 'stop_sell', label: 'Stop Sell', category: 'booking', input_type: 'boolean', default_value: false },
    { value: 'closed_to_arrival', label: 'Closed To Arrival (CTA)', category: 'booking', input_type: 'boolean', default_value: false },
    { value: 'closed_to_departure', label: 'Closed To Departure (CTD)', category: 'booking', input_type: 'boolean', default_value: false },
    { value: 'min_stay', label: 'Minimum Stay', category: 'booking', input_type: 'number', default_value: 1 },
    { value: 'max_stay', label: 'Maximum Stay', category: 'booking', input_type: 'number', default_value: 30 },
    { value: 'min_advance_booking', label: 'Minimum Advance Booking', category: 'booking', input_type: 'number', default_value: 0 },
    { value: 'max_advance_booking', label: 'Maximum Advance Booking', category: 'booking', input_type: 'number', default_value: 365 },
];
var InventoryPlanningService = /** @class */ (function () {
    function InventoryPlanningService() {
        var _a, _b, _c, _d;
        this.filterState = {
            restrictions: ['RATE_AND_AVAILABILITY'], // Default: show availability + rates
            roomTypes: [],
            ratePlans: [],
            dateRange: {
                from: ((_b = (_a = new Date().toISOString()) === null || _a === void 0 ? void 0 : _a.split('T')) === null || _b === void 0 ? void 0 : _b[0]) || new Date().toISOString().substring(0, 10),
                to: ((_d = (_c = new Date(Date.now() + 13 * 24 * 60 * 60 * 1000).toISOString()) === null || _c === void 0 ? void 0 : _c.split('T')) === null || _d === void 0 ? void 0 : _d[0]) || new Date(Date.now() + 13 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10), // 14 days
            }
        };
        this.interfaceData = null;
        this.calendarData = null;
        this.pendingChanges = new Map();
        this.currentPropertyId = null;
        this.onCalendarUpdateCallback = null;
    }
    /**
     * Load interface data using auxiliar endpoint
     */
    InventoryPlanningService.prototype.loadInterfaceData = function (propertyId) {
        return __awaiter(this, void 0, void 0, function () {
            var response, restrictionTypes, error_1;
            var _this = this;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, inventoryAuxiliarApi.getInterfaceData(propertyId)];
                    case 1:
                        response = _c.sent();
                        restrictionTypes = ((_b = (_a = response.restrictions) === null || _a === void 0 ? void 0 : _a.restriction_types) === null || _b === void 0 ? void 0 : _b.map(function (type) {
                            var _a, _b;
                            var inputType = type.input_type || (((_a = type.value) === null || _a === void 0 ? void 0 : _a.includes('stay')) || ((_b = type.value) === null || _b === void 0 ? void 0 : _b.includes('advance')) ? 'number' : 'boolean');
                            return {
                                value: type.value,
                                label: type.label,
                                category: _this.categorizeRestriction(type.value),
                                input_type: inputType,
                                default_value: type.default_value || (inputType === 'boolean' ? false : 1)
                            };
                        })) || RESTRICTION_OPTIONS;
                        this.interfaceData = {
                            properties: response.properties || [],
                            roomTypes: response.roomTypes || [],
                            ratePlans: response.ratePlans || [],
                            restrictionTypes: restrictionTypes
                        };
                        return [2 /*return*/, this.interfaceData];
                    case 2:
                        error_1 = _c.sent();
                        console.warn('Error loading interface data from auxiliar, using defaults:', error_1);
                        // Fallback to existing logic if auxiliar fails
                        return [2 /*return*/, this.loadInterfaceDataFallback(propertyId)];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Load calendar data based on current filters
     */
    InventoryPlanningService.prototype.loadCalendarData = function (propertyId) {
        return __awaiter(this, void 0, void 0, function () {
            var params, rawResponse, response, transformedData_1, error_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        params = {
                            property_id: propertyId,
                            room_types: this.filterState.roomTypes.length > 0 ? this.filterState.roomTypes : undefined,
                            rate_plans: this.filterState.ratePlans.length > 0 ? this.filterState.ratePlans : undefined,
                            restrictions: this.filterState.restrictions.length > 0 ? this.filterState.restrictions : undefined,
                            date_from: this.filterState.dateRange.from,
                            date_to: this.filterState.dateRange.to
                        };
                        return [4 /*yield*/, inventoryCalendarApi.getCalendarData(params)];
                    case 1:
                        rawResponse = _a.sent();
                        response = Array.isArray(rawResponse) ? rawResponse :
                            (rawResponse === null || rawResponse === void 0 ? void 0 : rawResponse.calendar_data) ||
                                (rawResponse === null || rawResponse === void 0 ? void 0 : rawResponse.data) ||
                                (rawResponse === null || rawResponse === void 0 ? void 0 : rawResponse.results) ||
                                [];
                        transformedData_1 = {
                            availability: {},
                            rates: {},
                            restrictions: {}
                        };
                        // Process each room type and rate plan
                        if (Array.isArray(response)) {
                            response.forEach(function (roomType) {
                                roomType.rate_plans.forEach(function (ratePlan) {
                                    // Initialize arrays for this rate plan
                                    if (!transformedData_1.rates[ratePlan.id]) {
                                        transformedData_1.rates[ratePlan.id] = [];
                                    }
                                    if (!transformedData_1.restrictions[ratePlan.id]) {
                                        transformedData_1.restrictions[ratePlan.id] = [];
                                    }
                                    // Initialize availability for room type
                                    if (!transformedData_1.availability[roomType.id]) {
                                        transformedData_1.availability[roomType.id] = [];
                                    }
                                    ratePlan.dates.forEach(function (dateData) {
                                        // Add availability data (indexed by room type)
                                        transformedData_1.availability[roomType.id].push({
                                            id: "".concat(roomType.id, "_").concat(dateData.date),
                                            room_type: roomType.id,
                                            date: dateData.date,
                                            total_rooms: dateData.availability + (dateData.reserved_rooms || 0),
                                            available_rooms: dateData.availability,
                                            reserved_rooms: dateData.reserved_rooms || 0,
                                            blocked_rooms: dateData.blocked_rooms || 0,
                                            out_of_order_rooms: dateData.out_of_order_rooms || 0,
                                            is_closed: dateData.availability === 0,
                                            is_available: dateData.availability > 0
                                        });
                                        // Add rate data (indexed by rate plan)
                                        if (dateData.rate !== ratePlan.default_rate) {
                                            transformedData_1.rates[ratePlan.id].push({
                                                id: "".concat(ratePlan.id, "_").concat(dateData.date),
                                                rate_plan: ratePlan.id,
                                                date: dateData.date,
                                                base_rate: dateData.rate,
                                                restriction_type: 'rate'
                                            });
                                        }
                                        // Add restriction data (indexed by rate plan)
                                        Object.entries(dateData.restrictions).forEach(function (_a) {
                                            var restrictionType = _a[0], value = _a[1];
                                            if (value !== null && value !== undefined) {
                                                var normalizedType = restrictionType.toUpperCase();
                                                var restrictionData = {
                                                    id: "".concat(ratePlan.id, "_").concat(dateData.date, "_").concat(normalizedType),
                                                    rate_plan: ratePlan.id,
                                                    date: dateData.date,
                                                    restriction_type: normalizedType,
                                                    min_stay: normalizedType.includes('MIN_STAY') ? Number(value) : null,
                                                    max_stay: normalizedType.includes('MAX_STAY') ? Number(value) : null,
                                                    min_advance_booking: normalizedType.includes('MIN_ADVANCE') ? Number(value) : null,
                                                    max_advance_booking: normalizedType.includes('MAX_ADVANCE') ? Number(value) : null,
                                                    is_active: typeof value === 'boolean' ? value : true
                                                };
                                                transformedData_1.restrictions[ratePlan.id].push(restrictionData);
                                            }
                                        });
                                    });
                                });
                            });
                        }
                        else {
                            console.warn('Response is not an array:', response);
                        }
                        this.calendarData = transformedData_1;
                        this.currentPropertyId = propertyId;
                        // Trigger callback to update UI
                        if (this.onCalendarUpdateCallback) {
                            this.onCalendarUpdateCallback();
                        }
                        return [2 /*return*/, this.calendarData];
                    case 2:
                        error_2 = _a.sent();
                        console.error('Error loading calendar data:', error_2);
                        throw error_2;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Fallback method for interface data loading
     */
    InventoryPlanningService.prototype.loadInterfaceDataFallback = function (propertyId) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, roomTypesData, ratePlansData, restrictionConstants, error_3;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, Promise.all([
                                propertyId ? inventoryService.getRoomTypes({ property: propertyId }) : Promise.resolve({ results: [] }),
                                propertyId ? inventoryService.getRatePlans({ property: propertyId }) : Promise.resolve({ results: [] }),
                                this.getRestrictionConstants()
                            ])];
                    case 1:
                        _a = _b.sent(), roomTypesData = _a[0], ratePlansData = _a[1], restrictionConstants = _a[2];
                        this.interfaceData = {
                            properties: [],
                            roomTypes: roomTypesData.results || [],
                            ratePlans: ratePlansData.results || [],
                            restrictionTypes: restrictionConstants
                        };
                        return [2 /*return*/, this.interfaceData];
                    case 2:
                        error_3 = _b.sent();
                        console.error('Error in fallback interface data loading:', error_3);
                        throw error_3;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Get restriction constants from backend
     */
    InventoryPlanningService.prototype.getRestrictionConstants = function () {
        return __awaiter(this, void 0, void 0, function () {
            var constants, error_4;
            var _this = this;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, constantsApi.getRestrictionConstants()];
                    case 1:
                        constants = _c.sent();
                        if (constants && constants.restriction_types) {
                            return [2 /*return*/, (_b = (_a = constants.restriction_types) === null || _a === void 0 ? void 0 : _a.map) === null || _b === void 0 ? void 0 : _b.call(_a, function (type) { return ({
                                    value: type.value,
                                    label: type.label,
                                    category: _this.categorizeRestriction(type.value)
                                }); })];
                        }
                        // Fallback to hardcoded options if no data from backend
                        return [2 /*return*/, RESTRICTION_OPTIONS];
                    case 2:
                        error_4 = _c.sent();
                        console.warn('Could not fetch restriction constants from backend, using defaults:', error_4);
                        return [2 /*return*/, RESTRICTION_OPTIONS];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Categorize restriction type
     */
    InventoryPlanningService.prototype.categorizeRestriction = function (restrictionType) {
        var rateTypes = ['rate'];
        var bookingTypes = [
            'stop_sell', 'closed_to_arrival', 'closed_to_departure',
            'min_stay', 'max_stay', 'min_advance_booking', 'max_advance_booking'
        ];
        if (rateTypes.includes(restrictionType))
            return 'rate';
        if (bookingTypes.includes(restrictionType))
            return 'booking';
        return 'booking'; // Default to booking for unknown types
    };
    /**
     * Set callback for calendar updates
     */
    InventoryPlanningService.prototype.setCalendarUpdateCallback = function (callback) {
        this.onCalendarUpdateCallback = callback;
    };
    /**
     * Update filter state without reloading data
     */
    InventoryPlanningService.prototype.setFilters = function (newFilters) {
        this.filterState = __assign(__assign({}, this.filterState), newFilters);
    };
    /**
     * Update filter state and reload calendar data
     */
    InventoryPlanningService.prototype.updateFilters = function (newFilters) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.filterState = __assign(__assign({}, this.filterState), newFilters);
                        if (!this.currentPropertyId) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.loadCalendarData(this.currentPropertyId)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Get current filter state
     */
    InventoryPlanningService.prototype.getFilters = function () {
        return __assign({}, this.filterState);
    };
    /**
     * Get filtered planning data based on current filters
     */
    InventoryPlanningService.prototype.getFilteredData = function () {
        var _this = this;
        if (!this.interfaceData || !this.calendarData)
            return null;
        // Filter room types
        var roomTypes = this.interfaceData.roomTypes || [];
        var filteredRoomTypes = this.filterState.roomTypes.length > 0
            ? roomTypes.filter(function (rt) { return _this.filterState.roomTypes.includes(rt.id); })
            : roomTypes;
        // Filter rate plans based on UI mode
        var filteredRatePlans = [];
        if (this.shouldShowRatePlans()) {
            var ratePlans = this.interfaceData.ratePlans || [];
            var roomTypeIds_1 = filteredRoomTypes.map(function (rt) { return rt.id; });
            filteredRatePlans = this.filterState.ratePlans.length > 0
                ? ratePlans.filter(function (rp) {
                    return _this.filterState.ratePlans.includes(rp.id) && roomTypeIds_1.includes(rp.room_type);
                })
                : ratePlans.filter(function (rp) { return roomTypeIds_1.includes(rp.room_type); });
        }
        return {
            interfaceData: __assign(__assign({}, this.interfaceData), { roomTypes: filteredRoomTypes, ratePlans: filteredRatePlans }),
            calendarData: this.calendarData
        };
    };
    /**
     * Get data for a specific cell in the calendar
     */
    InventoryPlanningService.prototype.getCellData = function (roomTypeId, ratePlanId, date, restrictionType) {
        var _a, _b, _c, _d, _e, _f;
        if (!this.calendarData) {
            return null;
        }
        // Check for pending changes first
        var pendingChange = this.getPendingChange(roomTypeId, ratePlanId, date, restrictionType);
        // Availability data
        if (roomTypeId && !ratePlanId && typeof roomTypeId === 'number') {
            var availabilityArray = (_a = this.calendarData.availability) === null || _a === void 0 ? void 0 : _a[roomTypeId];
            var availability = availabilityArray === null || availabilityArray === void 0 ? void 0 : availabilityArray.find(function (a) { return a.date === date; });
            return {
                type: 'availability',
                roomTypeId: roomTypeId,
                date: date,
                value: pendingChange ? pendingChange.newValue : ((availability === null || availability === void 0 ? void 0 : availability.available_rooms) || 0),
                editable: new Date(date) >= new Date(), // Only future dates editable
            };
        }
        // Rate data (from rate plans) - editable when Rate and Availability is selected
        if (ratePlanId && roomTypeId) {
            var ratePlans = ((_b = this.interfaceData) === null || _b === void 0 ? void 0 : _b.ratePlans) || [];
            var ratePlan = ratePlans.find(function (rp) { return rp.id === ratePlanId; });
            // Check if rates should be editable based on current filter
            var canEditRates = this.filterState.restrictions.includes('RATE_AND_AVAILABILITY') ||
                this.filterState.restrictions.includes('ALL_RESTRICTIONS');
            // Check for rate override in both rates and restrictions
            var ratesArray = (_c = this.calendarData.rates) === null || _c === void 0 ? void 0 : _c[ratePlanId];
            var restrictionsArray = (_d = this.calendarData.restrictions) === null || _d === void 0 ? void 0 : _d[ratePlanId];
            var customRate = ratesArray === null || ratesArray === void 0 ? void 0 : ratesArray.find(function (r) { return r.date === date; });
            var rateOverride = restrictionsArray === null || restrictionsArray === void 0 ? void 0 : restrictionsArray.find(function (r) { return r.date === date && r.restriction_type === 'rate'; });
            var displayValue = (ratePlan === null || ratePlan === void 0 ? void 0 : ratePlan.default_rate) || 0;
            // If there's a pending change, use that value
            if (pendingChange) {
                if (typeof pendingChange.newValue === 'object' && 'value' in pendingChange.newValue) {
                    displayValue = pendingChange.newValue.value;
                }
                else {
                    displayValue = pendingChange.newValue;
                }
            }
            else if (customRate && customRate.base_rate) {
                // Use custom rate from rates array
                displayValue = customRate.base_rate;
            }
            else if (rateOverride && rateOverride.base_rate) {
                // Use rate override from restrictions
                displayValue = rateOverride.base_rate;
            }
            else {
            }
            return {
                type: 'rate',
                roomTypeId: roomTypeId,
                ratePlanId: ratePlanId,
                date: date,
                value: displayValue,
                editable: canEditRates && new Date(date) >= new Date(), // Editable when rate mode is active
            };
        }
        // Restriction data
        if (ratePlanId && restrictionType && typeof ratePlanId === 'number') {
            var restrictionsArray = ((_f = (_e = this.calendarData) === null || _e === void 0 ? void 0 : _e.restrictions) === null || _f === void 0 ? void 0 : _f[ratePlanId]) || [];
            var restriction = restrictionsArray.find(function (r) { var _a; return r.date === date && ((_a = r.restriction_type) === null || _a === void 0 ? void 0 : _a.toUpperCase()) === (restrictionType === null || restrictionType === void 0 ? void 0 : restrictionType.toUpperCase()); });
            var restrictionOption = RESTRICTION_OPTIONS.find(function (opt) { return opt.value === restrictionType; });
            // Use input_type from restriction option if available, fallback to getBooleanRestrictionTypes
            var isBoolean = (restrictionOption === null || restrictionOption === void 0 ? void 0 : restrictionOption.input_type) === 'boolean' || getBooleanRestrictionTypes().includes(restrictionType);
            var currentValue = isBoolean
                ? ((restriction === null || restriction === void 0 ? void 0 : restriction.is_active) || false)
                : ((restriction === null || restriction === void 0 ? void 0 : restriction.min_stay) || (restriction === null || restriction === void 0 ? void 0 : restriction.max_stay) || (restriction === null || restriction === void 0 ? void 0 : restriction.min_advance_booking) || (restriction === null || restriction === void 0 ? void 0 : restriction.max_advance_booking) || 0);
            return {
                type: 'restriction',
                ratePlanId: ratePlanId,
                date: date,
                restrictionType: restrictionType,
                value: pendingChange ? pendingChange.newValue : currentValue,
                editable: new Date(date) >= new Date(),
            };
        }
        return null;
    };
    /**
     * Get restrictions that should be visible based on current filter
     */
    InventoryPlanningService.prototype.getVisibleRestrictions = function () {
        var restrictions = this.filterState.restrictions;
        // If no restrictions selected, return empty array
        if (restrictions.length === 0) {
            return [];
        }
        // Main UI control options - these are not actual restrictions
        if (restrictions.includes('ONLY_AVAILABILITY')) {
            return []; // Only show availability, no restrictions
        }
        if (restrictions.includes('RATE_AND_AVAILABILITY')) {
            return []; // Show availability + rates, no restrictions by default
        }
        if (restrictions.includes('ALL_RESTRICTIONS')) {
            return RESTRICTION_OPTIONS; // Show all available Channex restrictions
        }
        // Show specific selected Channex restrictions only
        return RESTRICTION_OPTIONS.filter(function (option) {
            return restrictions.includes(option.value);
        });
    };
    /**
     * Check if rate plans should be shown based on current filter
     */
    InventoryPlanningService.prototype.shouldShowRatePlans = function () {
        var restrictions = this.filterState.restrictions;
        // Only Availability mode hides rate plans
        if (restrictions.includes('ONLY_AVAILABILITY')) {
            return false;
        }
        // All other modes show rate plans
        return true;
    };
    /**
     * Add change to pending batch (don't save immediately)
     */
    InventoryPlanningService.prototype.addPendingChange = function (cellData, newValue) {
        var changeKey = "".concat(cellData.type, "-").concat(cellData.roomTypeId || '', "-").concat(cellData.ratePlanId || '', "-").concat(cellData.date, "-").concat(cellData.restrictionType || '');
        this.pendingChanges.set(changeKey, {
            cellData: cellData,
            newValue: newValue,
            timestamp: Date.now()
        });
    };
    /**
     * Get count of pending changes
     */
    InventoryPlanningService.prototype.getPendingChangesCount = function () {
        return this.pendingChanges.size;
    };
    /**
     * Get pending changes for display
     */
    InventoryPlanningService.prototype.getPendingChanges = function () {
        return Array.from(this.pendingChanges.values());
    };
    /**
     * Clear all pending changes
     */
    InventoryPlanningService.prototype.clearPendingChanges = function () {
        this.pendingChanges.clear();
    };
    /**
     * Get pending change for specific cell
     */
    InventoryPlanningService.prototype.getPendingChange = function (roomTypeId, ratePlanId, date, restrictionType) {
        var changeKey = "".concat(restrictionType ? 'restriction' : ratePlanId ? 'rate' : 'availability', "-").concat(roomTypeId || '', "-").concat(ratePlanId || '', "-").concat(date, "-").concat(restrictionType || '');
        return this.pendingChanges.get(changeKey);
    };
    /**
     * Check if cell has pending changes
     */
    InventoryPlanningService.prototype.hasPendingChange = function (roomTypeId, ratePlanId, date, restrictionType) {
        return this.getPendingChange(roomTypeId, ratePlanId, date, restrictionType) !== undefined;
    };
    /**
     * Save all pending changes in batch
     */
    InventoryPlanningService.prototype.savePendingChanges = function () {
        return __awaiter(this, void 0, void 0, function () {
            var results, changes, availabilityChanges, restrictionChanges, _loop_1, this_1, _i, changes_1, change, bulkChanges, bulkResult, hasErrors, hadSuccess, error_5;
            var _this = this;
            var _a, _b, _c, _d, _e, _f;
            return __generator(this, function (_g) {
                switch (_g.label) {
                    case 0:
                        results = { success: 0, failed: 0, errors: [] };
                        changes = Array.from(this.pendingChanges.values());
                        if (changes.length === 0) {
                            return [2 /*return*/, results];
                        }
                        _g.label = 1;
                    case 1:
                        _g.trys.push([1, 6, , 7]);
                        availabilityChanges = [];
                        restrictionChanges = [];
                        _loop_1 = function (change) {
                            var cellData = change.cellData, newValue = change.newValue;
                            if (cellData.type === 'availability' && cellData.roomTypeId && typeof cellData.roomTypeId === 'number') {
                                // Always send room_type_id and date (required by backend)
                                availabilityChanges.push({
                                    room_type_id: cellData.roomTypeId,
                                    date: cellData.date,
                                    available_rooms: Number(newValue)
                                });
                            }
                            else if (cellData.type === 'rate' && cellData.ratePlanId) {
                                // Handle rate changes as rate restrictions
                                // Validate newValue is not null/undefined
                                if (newValue === null || newValue === undefined || newValue === '') {
                                    console.warn('Skipping rate change with null/undefined value:', { cellData: cellData, newValue: newValue });
                                    return "continue";
                                }
                                // Handle rate with currency information (same logic as updateSingleCell)
                                var rateValue = newValue;
                                var currency = 'USD'; // Default
                                // Check if newValue contains currency information
                                if (typeof newValue === 'object' && newValue !== null && 'value' in newValue) {
                                    var rateWithCurrency = newValue;
                                    rateValue = rateWithCurrency.value;
                                    currency = rateWithCurrency.currency;
                                }
                                var rateRestriction = {
                                    rate_plan_id: cellData.ratePlanId,
                                    date: cellData.date,
                                    restriction_type: 'rate',
                                    value: Number(rateValue), // Use extracted rate value
                                    currency: currency, // Include currency
                                    is_active: true
                                };
                                // Check if rate restriction already exists
                                var existingRateRestriction = cellData.ratePlanId && typeof cellData.ratePlanId === 'number'
                                    ? (_c = (_b = (_a = this_1.calendarData) === null || _a === void 0 ? void 0 : _a.restrictions) === null || _b === void 0 ? void 0 : _b[cellData.ratePlanId]) === null || _c === void 0 ? void 0 : _c.find(function (r) { return r.date === cellData.date && r.restriction_type === 'rate'; })
                                    : undefined;
                                if (existingRateRestriction) {
                                    rateRestriction.id = existingRateRestriction.id;
                                }
                                restrictionChanges.push(rateRestriction);
                            }
                            else if (cellData.type === 'restriction' && cellData.restrictionType && cellData.ratePlanId) {
                                // Handle other restrictions
                                var restrictionType_1 = cellData.restrictionType.replace(/['"]/g, '');
                                var restrictionData = {
                                    rate_plan_id: cellData.ratePlanId,
                                    date: cellData.date,
                                    restriction_type: restrictionType_1
                                };
                                // For boolean restrictions, use value field for is_active
                                // Check if restriction is boolean type using dynamic lookup
                                var restrictionOptions = RESTRICTION_OPTIONS;
                                var restrictionOption = restrictionOptions.find(function (opt) { return opt.value === restrictionType_1 || opt.value === restrictionType_1.toLowerCase(); });
                                var isBooleanType = (restrictionOption === null || restrictionOption === void 0 ? void 0 : restrictionOption.input_type) === 'boolean' || getBooleanRestrictionTypes().includes(restrictionType_1);
                                if (isBooleanType) {
                                    restrictionData.value = typeof newValue === 'boolean' ? newValue : Boolean(newValue);
                                }
                                else {
                                    // For numeric restrictions, use value field for the numeric value
                                    restrictionData.value = Number(newValue);
                                }
                                // Check if restriction already exists
                                var existingRestriction = cellData.ratePlanId && typeof cellData.ratePlanId === 'number'
                                    ? (_f = (_e = (_d = this_1.calendarData) === null || _d === void 0 ? void 0 : _d.restrictions) === null || _e === void 0 ? void 0 : _e[cellData.ratePlanId]) === null || _f === void 0 ? void 0 : _f.find(function (r) { return r.date === cellData.date && r.restriction_type === restrictionType_1; })
                                    : undefined;
                                if (existingRestriction) {
                                    restrictionData.id = existingRestriction.id;
                                }
                                restrictionChanges.push(restrictionData);
                            }
                        };
                        this_1 = this;
                        for (_i = 0, changes_1 = changes; _i < changes_1.length; _i++) {
                            change = changes_1[_i];
                            _loop_1(change);
                        }
                        // Execute bulk save using the unified endpoint
                        // Validate that we have a property ID
                        if (!this.currentPropertyId) {
                            throw new Error('Property ID is required for bulk save operations');
                        }
                        bulkChanges = {
                            property_id: this.currentPropertyId, // Add property_id to bulk save
                            availability: availabilityChanges.length > 0 ? availabilityChanges : undefined,
                            restrictions: restrictionChanges.length > 0 ? restrictionChanges : undefined
                        };
                        return [4 /*yield*/, inventoryCalendarApi.saveBulkChanges(bulkChanges)];
                    case 2:
                        bulkResult = _g.sent();
                        results.success = bulkResult.success || 0;
                        results.failed = bulkResult.failed || 0;
                        results.errors = bulkResult.errors || [];
                        hasErrors = Array.isArray(results.errors) && results.errors.length > 0;
                        hadSuccess = results.success > 0;
                        if (!(!hasErrors && hadSuccess)) return [3 /*break*/, 5];
                        this.clearPendingChanges();
                        if (!this.currentPropertyId) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.loadCalendarData(this.currentPropertyId)];
                    case 3:
                        _g.sent();
                        // Force UI update with small delay to ensure data propagation
                        setTimeout(function () {
                            if (_this.onCalendarUpdateCallback) {
                                _this.onCalendarUpdateCallback();
                            }
                        }, 100);
                        _g.label = 4;
                    case 4: return [3 /*break*/, 5];
                    case 5: return [3 /*break*/, 7];
                    case 6:
                        error_5 = _g.sent();
                        results.failed = changes.length;
                        results.errors.push({
                            type: 'general',
                            error: error_5 instanceof Error ? error_5.message : 'Unknown error'
                        });
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/, results];
                }
            });
        });
    };
    /**
     * Update single cell value (internal method)
     */
    InventoryPlanningService.prototype.updateSingleCell = function (cellData, newValue) {
        return __awaiter(this, void 0, void 0, function () {
            var existingAvailability, rateValue, currency, rateWithCurrency, restrictionData, existingRateRestriction, existingRestriction, cleanRestrictionType, restrictionData, error_6;
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            return __generator(this, function (_k) {
                switch (_k.label) {
                    case 0:
                        _k.trys.push([0, 16, , 17]);
                        if (!(cellData.type === 'availability' && cellData.roomTypeId)) return [3 /*break*/, 5];
                        existingAvailability = cellData.roomTypeId && typeof cellData.roomTypeId === 'number'
                            ? (_c = (_b = (_a = this.calendarData) === null || _a === void 0 ? void 0 : _a.availability) === null || _b === void 0 ? void 0 : _b[cellData.roomTypeId]) === null || _c === void 0 ? void 0 : _c.find(function (a) { return a.date === cellData.date; })
                            : undefined;
                        if (!existingAvailability) return [3 /*break*/, 2];
                        return [4 /*yield*/, inventoryService.updateAvailability(existingAvailability.id, {
                                available_rooms: Number(newValue)
                            })];
                    case 1:
                        _k.sent();
                        return [3 /*break*/, 4];
                    case 2: return [4 /*yield*/, inventoryService.createAvailability({
                            room_type: cellData.roomTypeId,
                            date: cellData.date,
                            total_rooms: Number(newValue),
                            available_rooms: Number(newValue),
                            reserved_rooms: 0,
                            blocked_rooms: 0,
                            out_of_order_rooms: 0,
                            is_closed: false,
                            is_available: true
                        })];
                    case 3:
                        _k.sent();
                        _k.label = 4;
                    case 4: return [2 /*return*/, true];
                    case 5:
                        if (!(cellData.type === 'rate' && cellData.ratePlanId)) return [3 /*break*/, 10];
                        rateValue = newValue;
                        currency = 'USD';
                        // Check if newValue contains currency information
                        if (typeof newValue === 'object' && newValue !== null && 'value' in newValue) {
                            rateWithCurrency = newValue;
                            rateValue = rateWithCurrency.value;
                            currency = rateWithCurrency.currency;
                        }
                        restrictionData = {
                            rate_plan: cellData.ratePlanId,
                            date: cellData.date,
                            restriction_type: 'rate', // Correct Channex type for rates
                            base_rate: Number(rateValue),
                            currency: currency,
                            is_active: true
                        };
                        existingRateRestriction = cellData.ratePlanId && typeof cellData.ratePlanId === 'number'
                            ? (_f = (_e = (_d = this.calendarData) === null || _d === void 0 ? void 0 : _d.restrictions) === null || _e === void 0 ? void 0 : _e[cellData.ratePlanId]) === null || _f === void 0 ? void 0 : _f.find(function (r) { return r.date === cellData.date && r.restriction_type === 'rate'; })
                            : undefined;
                        if (!existingRateRestriction) return [3 /*break*/, 7];
                        return [4 /*yield*/, inventoryService.updateRestriction(existingRateRestriction.id, restrictionData)];
                    case 6:
                        _k.sent();
                        return [3 /*break*/, 9];
                    case 7: return [4 /*yield*/, inventoryService.createRestriction(restrictionData)];
                    case 8:
                        _k.sent();
                        _k.label = 9;
                    case 9: return [2 /*return*/, true];
                    case 10:
                        if (!(cellData.type === 'restriction' && cellData.ratePlanId && cellData.restrictionType)) return [3 /*break*/, 15];
                        existingRestriction = cellData.ratePlanId && cellData.restrictionType && typeof cellData.ratePlanId === 'number'
                            ? (_j = (_h = (_g = this.calendarData) === null || _g === void 0 ? void 0 : _g.restrictions) === null || _h === void 0 ? void 0 : _h[cellData.ratePlanId]) === null || _j === void 0 ? void 0 : _j.find(function (r) { return r.date === cellData.date && r.restriction_type === cellData.restrictionType; })
                            : undefined;
                        cleanRestrictionType = cellData.restrictionType.replace(/['"]/g, '');
                        restrictionData = {
                            rate_plan: cellData.ratePlanId,
                            date: cellData.date,
                            restriction_type: cleanRestrictionType,
                            is_active: typeof newValue === 'boolean' ? newValue : true
                        };
                        if (typeof newValue === 'number') {
                            if (cellData.restrictionType === 'min_stay') {
                                restrictionData.min_stay = newValue;
                            }
                            else if (cellData.restrictionType === 'max_stay') {
                                restrictionData.max_stay = newValue;
                            }
                            else if (cellData.restrictionType === 'min_advance_booking') {
                                restrictionData.min_advance_booking = newValue;
                            }
                            else if (cellData.restrictionType === 'max_advance_booking') {
                                restrictionData.max_advance_booking = newValue;
                            }
                            else if (cellData.restrictionType === 'rate') {
                                restrictionData.base_rate = newValue;
                            }
                        }
                        if (!existingRestriction) return [3 /*break*/, 12];
                        return [4 /*yield*/, inventoryService.updateRestriction(existingRestriction.id, restrictionData)];
                    case 11:
                        _k.sent();
                        return [3 /*break*/, 14];
                    case 12: return [4 /*yield*/, inventoryService.createRestriction(restrictionData)];
                    case 13:
                        _k.sent();
                        _k.label = 14;
                    case 14: return [2 /*return*/, true];
                    case 15: return [2 /*return*/, false];
                    case 16:
                        error_6 = _k.sent();
                        console.error('Error updating cell value:', error_6);
                        throw error_6;
                    case 17: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Generate date range for calendar
     */
    InventoryPlanningService.prototype.getDateRange = function () {
        var _a, _b;
        var dates = [];
        var start = new Date(this.filterState.dateRange.from);
        var end = new Date(this.filterState.dateRange.to);
        for (var d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            var dateStr = ((_b = (_a = d.toISOString()) === null || _a === void 0 ? void 0 : _a.split('T')) === null || _b === void 0 ? void 0 : _b[0]) || d.toISOString().substring(0, 10);
            dates.push(dateStr);
        }
        return dates;
    };
    /**
     * Refresh data for current filters
     */
    InventoryPlanningService.prototype.refreshData = function (propertyId) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.loadInterfaceData(propertyId)];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.loadCalendarData(propertyId)];
                    case 2:
                        _a.sent();
                        return [2 /*return*/, this.getFilteredData()];
                }
            });
        });
    };
    return InventoryPlanningService;
}());
// Singleton instance
export var inventoryPlanningService = new InventoryPlanningService();
// Helper functions for restriction types
export function getBooleanRestrictionTypes() {
    // Get boolean types from loaded restriction options
    var restrictionOptions = RESTRICTION_OPTIONS;
    var booleanTypes = restrictionOptions
        .filter(function (option) { return option.input_type === 'boolean'; })
        .map(function (option) { return option.value; });
    // Add common variations
    var variations = booleanTypes.flatMap(function (type) { return [
        type,
        type.toUpperCase(),
        type.toLowerCase()
    ]; });
    return Array.from(new Set(variations));
}
export function getNumericRestrictionTypes() {
    return [
        'MIN_STAY', 'MAX_STAY', 'MIN_ADVANCE_BOOKING', 'MAX_ADVANCE_BOOKING',
        // Also support lowercase/underscore variants
        'min_stay', 'max_stay', 'min_advance_booking', 'max_advance_booking'
    ];
}
// Helper function to format restriction values for display
export function formatRestrictionValue(restrictionType, value) {
    if (typeof value === 'boolean') {
        return value ? '✓' : '□';
    }
    if (typeof value === 'number') {
        // Just return the number without suffixes (N or D)
        return value.toString();
    }
    return String(value);
}
// Helper function to get restriction display color
export function getRestrictionColor(restrictionType) {
    var colors = {
        // Valid Channex restriction types
        'rate': '#ffa500',
        'stop_sell': '#ff3838',
        'closed_to_arrival': '#ff4757',
        'closed_to_departure': '#ff6b7d',
        'min_stay': '#3742fa',
        'max_stay': '#2f3542',
        'min_advance_booking': '#1e90ff',
        'max_advance_booking': '#4169e1',
    };
    return colors[restrictionType] || '#747d8c';
}
